
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SECTORS, LABEL_LEGAL_FORMS } from './constants';

interface CompanyDetailsProps {
  formState: Record<string, any>;
  setFormState: (state: Record<string, any>) => void;
  onValidityChange: (isValid: boolean) => void;
  readOnly?: boolean;
}

const CompanyDetails = ({ formState, setFormState, onValidityChange, readOnly = false }: CompanyDetailsProps) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formState?.companyName?.trim()) newErrors.companyName = "Le nom de la société est requis";
    if (!formState?.sector) newErrors.sector = "Le secteur est requis";
    if (!formState?.legalForm) newErrors.legalForm = "La forme juridique est requise";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const isValid = validate();
    onValidityChange(isValid);
  }, [formState]);

  const handleChange = (field: string, value: any) => {
    if (readOnly) return;
    
    if (field === "sector") {
      // When sector is changed, update both sector and secteurs_activite
      setFormState((prev: Record<string, any>) => ({
        ...prev,
        [field]: value,
        // Update secteurs_activite as an array with the selected sector
        secteurs_activite: [value]
      }));
    } else {
      setFormState((prev: Record<string, any>) => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Check if the current legal form is valid for the label submission
  // This helps handle cases where the legal form from eligibility might not be in our label form options
  const isValidLegalForm = (form: string) => LABEL_LEGAL_FORMS.includes(form);

  // Determine what to display in the sector dropdown
  // Try to get sector from various potential sources in the formState
  const determineSector = () => {
    // First check if we have a direct sector value
    if (formState?.sector) {
      return formState.sector;
    }
    
    // Then check if we have secteurs_activite array with values
    if (Array.isArray(formState?.secteurs_activite) && formState.secteurs_activite.length > 0) {
      return formState.secteurs_activite[0];
    }
    
    // Finally check if we have a sectors array (from eligibility form)
    if (Array.isArray(formState?.sectors) && formState.sectors.length > 0) {
      return formState.sectors[0];
    }
    
    // Return empty string if no sector found
    return "";
  };

  const displaySector = determineSector();

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Informations société</h3>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="companyName">
            Quel est le nom de votre société ? <span className="text-red-500">*</span>
          </Label>
          <Input
            id="companyName"
            value={formState?.companyName || ""}
            onChange={(e) => handleChange("companyName", e.target.value)}
            className={readOnly ? "bg-gray-100" : ""}
            readOnly={readOnly}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="sector">
            Quel est le secteur d'activité de l'entreprise ? <span className="text-red-500">*</span>
          </Label>
          {readOnly ? (
            <div className="p-2 bg-gray-100 border border-gray-300 rounded">
              {displaySector || "Non spécifié"}
            </div>
          ) : (
            <Select
              value={displaySector}
              onValueChange={(value) => handleChange("sector", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionnez un secteur" />
              </SelectTrigger>
              <SelectContent>
                {SECTORS.map((sector) => (
                  <SelectItem key={sector} value={sector}>
                    {sector}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {errors.sector && <p className="text-sm text-red-500">{errors.sector}</p>}
        </div>

        <div className="space-y-3">
          <Label>
            Quelle est la forme juridique de votre structure ? <span className="text-red-500">*</span>
          </Label>
          <RadioGroup
            value={formState?.legalForm || ""}
            onValueChange={(value) => handleChange("legalForm", value)}
            disabled={readOnly}
          >
            <div className="grid grid-cols-2 gap-3">
              {LABEL_LEGAL_FORMS.map((formType) => (
                <div key={formType} className="flex items-center space-x-2">
                  <RadioGroupItem 
                    value={formType} 
                    id={`legal-${formType}`} 
                    disabled={readOnly} 
                    className={formState?.legalForm === formType ? "text-primary border-primary" : ""}
                  />
                  <Label htmlFor={`legal-${formType}`}>{formType}</Label>
                </div>
              ))}
            </div>
          </RadioGroup>
          {!isValidLegalForm(formState?.legalForm) && formState?.legalForm && (
            <p className="text-sm text-amber-600">
              La forme juridique "{formState.legalForm}" n'est pas éligible pour le label. Veuillez sélectionner une forme juridique valide.
            </p>
          )}
          {errors.legalForm && <p className="text-sm text-red-500">{errors.legalForm}</p>}
        </div>
      </div>
    </div>
  );
};

export default CompanyDetails;
